import { getChainConfigEntry } from '@/config/chain'
import { matchOpportunityToCluster } from '@/config/market-config'
import { sortByAPY, sortByUsdValue } from '@/domain/common/sorters'
import { OpenDialogFunction } from '@/domain/hooks/useDialog'
import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { useCardSolverConfigs, useStoreHydrated } from '@/domain/state'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { Token } from '@/domain/types/Token'
import { CardRouteBadge } from '@/features/card/components/CardRouteBadge'
import { getPooledSpendCardRouteBadge } from '@/features/card/utils/cardSolverRouteMatching'
import { collateralDialogConfig } from '@/features/dialogs/collateral/CollateralDialog'
import { withdrawDialogConfig } from '@/features/dialogs/withdraw/WithdrawDialog'
import { ELendVaultData, useELend } from '@/features/elend/logic/useELend'
import { ApyWithRewardsCell } from '@/features/my-portfolio/components/ApyWithRewardsCell'
import {
  PooledMigrationTarget,
  pooledMigrationDialogConfig,
} from '@/features/my-portfolio/components/pooled-migration-dialog/PooledMigrationDialog'
import { EVaultData, useEVaults } from '@/features/vaults/logic/useEVaults'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Tooltip, TooltipContentShort, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { ApyTooltip } from '@/ui/molecules/apy-tooltip/ApyTooltip'
import { ActionsCell } from '@/ui/molecules/data-table/components/ActionsCell'
import { CompactValueCell } from '@/ui/molecules/data-table/components/CompactValueCell'
import { SwitchCell } from '@/ui/molecules/data-table/components/SwitchCell'
import { TokenWithLogo } from '@/ui/molecules/data-table/components/TokenWithLogo'
import { ColumnDefinition } from '@/ui/molecules/data-table/types'
import { ResponsiveDataTable } from '@/ui/organisms/responsive-data-table/ResponsiveDataTable'
import { mockNativeToWHYPEOverride } from '@/utils/address'
import { useMemo, useRef } from 'react'
import { useAccount, useChainId } from 'wagmi'
import { Deposit } from '../../logic/assets'

export interface MyDepositsTableProps {
  assets: Deposit[]
  openDialog: OpenDialogFunction
}

function MigrationButton({
  token,
  deposit,
  target,
  loading,
  openDialog,
}: {
  token: Token
  deposit: Deposit['deposit']
  target: PooledMigrationTarget | undefined
  loading: boolean
  openDialog: OpenDialogFunction
}) {
  const disabled = deposit.isZero() || loading || !target

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="w-full md:w-fit">
          <Button
            variant="secondary"
            size="sm"
            className="w-full md:w-fit"
            disabled={disabled}
            onClick={() => {
              if (!target) return
              openDialog(pooledMigrationDialogConfig, { token, target })
            }}
          >
            Migrate
          </Button>
        </span>
      </TooltipTrigger>
      {disabled && (
        <TooltipContentShort>
          {loading ? 'Checking Euler markets' : target ? 'No pooled deposit to migrate' : 'No matching Euler market'}
        </TooltipContentShort>
      )}
    </Tooltip>
  )
}

export function MyDepositsTable({ assets, openDialog }: MyDepositsTableProps) {
  const { address } = useAccount()
  const { vaults: earnVaults, isLoading: isEarnVaultsLoading } = useEVaults(address)
  const { vaults: lendVaults, isLoading: isLendVaultsLoading } = useELend(address, {
    includeSubAccountPositions: false,
  })
  const { getByAddress, error: pointsDataError, isLoading: isPointsDataLoading } = usePointsData()
  const { byAddress: cardSolverConfigsByAddress } = useCardSolverConfigs()
  const isStoreHydrated = useStoreHydrated()
  const hasPointsError = !isPointsDataLoading && !!pointsDataError?.message
  const chainId = useChainId()
  const { pooledPartnerPoints } = getChainConfigEntry(chainId)
  const cardSolverConfigs = useMemo(
    () => (isStoreHydrated && address ? Object.values(cardSolverConfigsByAddress[address.toLowerCase()] ?? {}) : []),
    [address, cardSolverConfigsByAddress, isStoreHydrated],
  )
  const migrationTargetsByTokenAddress = useMemo(() => {
    const targets = new Map<string, PooledMigrationTarget | undefined>()

    for (const asset of assets) {
      targets.set(asset.token.address.toLowerCase(), getMigrationTarget(asset.token, earnVaults, lendVaults))
    }

    return targets
  }, [assets, earnVaults, lendVaults])
  const migrationTargetsLoading = isEarnVaultsLoading || isLendVaultsLoading
  const migrationStateRef = useRef({
    targetsByTokenAddress: migrationTargetsByTokenAddress,
    loading: migrationTargetsLoading,
  })
  migrationStateRef.current = {
    targetsByTokenAddress: migrationTargetsByTokenAddress,
    loading: migrationTargetsLoading,
  }

  const columnDefinition = useMemo<{ [key: string]: ColumnDefinition<Deposit> }>(
    () => ({
      symbol: {
        header: 'Assets',
        renderCell: ({ token, reserveStatus, isCombinedBalance, aTokenAddress }) => {
          const displayToken = getDisplayToken(chainId, token)
          const cardRouteBadge = getPooledSpendCardRouteBadge(cardSolverConfigs, aTokenAddress)
          return (
            <div className="flex items-center gap-2">
              <TokenWithLogo
                token={displayToken}
                reserveStatus={reserveStatus}
                isCombinedBalance={isCombinedBalance}
                hideName
              />
              <CardRouteBadge badge={cardRouteBadge} />
            </div>
          )
        },
      },
      deposit: {
        header: 'Balance',
        sortable: true,
        sortingFn: (a, b) => sortByUsdValue(a.original, b.original, 'deposit'),
        headerAlign: 'right',
        showOnMobile: true,
        renderCell: ({ token, deposit }, mobileViewOptions) => (
          <CompactValueCell token={token} value={deposit} mobileViewOptions={mobileViewOptions} hideEmpty />
        ),
      },
      apy: {
        header: <ApyTooltip variant="supply">APY</ApyTooltip>,
        headerAlign: 'right',
        sortable: true,
        showOnMobile: true,
        sortingFn: (a, b) => sortByAPY(a.original.supplyAPY, b.original.supplyAPY),
        renderCell: ({ supplyAPY, token }, mobileViewOptions) => {
          const points = getByAddress({
            type: 'pooled',
            action: 'deposit',
            address: mockNativeToWHYPEOverride(token.address),
          })
          return (
            <ApyWithRewardsCell
              action="deposit"
              asset={token.symbol}
              apy={supplyAPY}
              points={points}
              mobileViewOptions={mobileViewOptions}
              pointsError={hasPointsError}
              pointsLoading={isPointsDataLoading}
              partnerPoints={pooledPartnerPoints[CheckedAddress(token.address)]?.points}
            />
          )
        },
      },
      collateral: {
        header: 'Collateral',
        headerAlign: 'right',
        renderCell: ({ isUsedAsCollateral, token }, mobileViewOptions) => (
          <SwitchCell
            checked={isUsedAsCollateral}
            onSwitchClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              openDialog(collateralDialogConfig, {
                useAsCollateral: !isUsedAsCollateral,
                token,
              })
            }}
            mobileViewOptions={mobileViewOptions}
          />
        ),
      },
      actions: {
        header: '',
        renderCell: ({ token, deposit }) => {
          const migrationTarget = migrationStateRef.current.targetsByTokenAddress.get(token.address.toLowerCase())

          return (
            <ActionsCell>
              <Button
                variant="outline"
                size="sm"
                className="w-full md:w-fit"
                disabled={deposit.isZero()}
                onClick={() => {
                  openDialog(withdrawDialogConfig, { token })
                }}
              >
                Withdraw
              </Button>
              <MigrationButton
                token={token}
                deposit={deposit}
                target={migrationTarget}
                loading={migrationStateRef.current.loading}
                openDialog={openDialog}
              />
            </ActionsCell>
          )
        },
      },
    }),
    [openDialog, getByAddress, hasPointsError, isPointsDataLoading, pooledPartnerPoints, chainId, cardSolverConfigs],
  )
  return (
    <Panel
      collapsibleOptions={{ collapsible: true, collapsibleAbove: 'md', fullHeight: true }}
      className="bg-panel xl:h-full md:px-3"
    >
      <Panel.Header>
        <Panel.Title className="text-xl md:px-3" gradient>
          My deposits
        </Panel.Title>
      </Panel.Header>

      <Panel.Content>
        <ResponsiveDataTable
          gridTemplateColumnsClassName="grid-cols-[repeat(4,_3fr)_5fr] lg:grid-cols-[repeat(3,_2fr)_1fr_3fr]"
          columnDefinition={columnDefinition}
          data={assets}
        />
      </Panel.Content>
    </Panel>
  )
}

function getMigrationTarget(
  token: Token,
  earnVaults: EVaultData[],
  lendVaults: ELendVaultData[],
): PooledMigrationTarget | undefined {
  const tokenAddress = mockNativeToWHYPEOverride(token.address).toLowerCase()
  const matchingEarnVault = earnVaults.find(
    (vault) =>
      vault.underlyingAsset?.toLowerCase() === tokenAddress &&
      matchOpportunityToCluster({ source: 'evault', address: vault.address }, 'earn'),
  )

  if (matchingEarnVault?.underlyingAsset) {
    return {
      label: 'Earn',
      vaultType: 'eEarn',
      vault: { address: matchingEarnVault.address, underlyingAsset: matchingEarnVault.underlyingAsset },
    }
  }

  const matchingPrimeVault = lendVaults.find(
    (vault) =>
      vault.underlyingAsset?.toLowerCase() === tokenAddress &&
      matchOpportunityToCluster({ source: 'elend', market: vault.market }, 'prime'),
  )

  if (matchingPrimeVault?.underlyingAsset) {
    return {
      label: 'Prime',
      vaultType: 'eLend',
      vault: { address: matchingPrimeVault.address, underlyingAsset: matchingPrimeVault.underlyingAsset },
    }
  }

  const matchingYieldVault = lendVaults.find(
    (vault) =>
      vault.underlyingAsset?.toLowerCase() === tokenAddress &&
      matchOpportunityToCluster({ source: 'elend', market: vault.market }, 'yield'),
  )

  if (matchingYieldVault?.underlyingAsset) {
    return {
      label: 'Yield',
      vaultType: 'eLend',
      vault: { address: matchingYieldVault.address, underlyingAsset: matchingYieldVault.underlyingAsset },
    }
  }
}
